/**
 * Centralized date calculation utilities
 * Provides consistent date calculations across the application
 */

/** Local calendar day start as epoch ms. */
function startOfLocalDayMs(referenceDate = new Date()) {
  if (typeof referenceDate === 'number' && Number.isFinite(referenceDate)) {
    return referenceDate;
  }
  const d =
    referenceDate instanceof Date
      ? new Date(referenceDate.getTime())
      : new Date(referenceDate);
  if (Number.isNaN(d.getTime())) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback.getTime();
  }
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Expiration as local calendar-day epoch ms — YYYY-MM-DD is local, not UTC.
 * @param {string|Date|null|undefined} expirationDate
 * @returns {number|null}
 */
function expirationDayMs(expirationDate) {
  if (expirationDate == null || expirationDate === '') return null;
  if (typeof expirationDate === 'string') {
    const raw = expirationDate.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const y = Number(raw.slice(0, 4));
      const m = Number(raw.slice(5, 7)) - 1;
      const day = Number(raw.slice(8, 10));
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
      return new Date(y, m, day).getTime();
    }
  }
  const exp = new Date(expirationDate);
  if (Number.isNaN(exp.getTime())) return null;
  exp.setHours(0, 0, 0, 0);
  return exp.getTime();
}

/**
 * Calculate days until expiry for a given expiration date (local calendar days).
 * @param {string|Date} expirationDate - The expiration date
 * @returns {number|null} - Days until expiry (negative if expired), null if no date
 */
export const calculateDaysUntilExpiry = (expirationDate) => {
  const expMs = expirationDayMs(expirationDate);
  if (expMs == null) return null;
  const todayMs = startOfLocalDayMs(new Date());
  return Math.round((expMs - todayMs) / (1000 * 60 * 60 * 24));
};

/**
 * Check if a product is expired (after the expiration calendar day).
 * Aligns with isLotExpired: sellable on the expiration day itself.
 * @param {string|Date} expirationDate - The expiration date
 * @returns {boolean} - True if expired, false otherwise
 */
export const isExpired = (expirationDate) => {
  const days = calculateDaysUntilExpiry(expirationDate);
  return days != null && days < 0;
};

/**
 * Check if a product is expiring soon (within specified days)
 * @deprecated Prefer domain/inventory/expiry.isExpiringSoon (Conservation-Schwellen).
 * @param {string|Date} expirationDate - The expiration date
 * @param {number} daysThreshold - Number of days to consider "soon"
 * @returns {boolean} - True if expiring soon, false otherwise
 */
export const isExpiringSoon = (expirationDate, daysThreshold = 7) => {
  const daysUntilExpiry = calculateDaysUntilExpiry(expirationDate);
  return daysUntilExpiry != null && daysUntilExpiry > 0 && daysUntilExpiry <= daysThreshold;
};
